import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { messagingAPI } from '../api/client';

const UnreadMessagesContext = createContext({ unreadCount: 0, refresh: () => {} });

const POLL_INTERVAL_MS = 30_000;

/**
 * Background polling for global unread-message count.
 *
 * - Only polls when a non-admin user is logged in.
 * - Pauses while document is hidden (battery / quota friendly).
 * - Resumes & refreshes immediately on visibility change.
 */
export const UnreadMessagesProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      return;
    }
    try {
      const { data } = await messagingAPI.getUnreadCount();
      if (mountedRef.current) {
        setUnreadCount(typeof data?.total === 'number' ? data.total : 0);
      }
    } catch (_) {
      // Silently ignore — interval will retry.
    }
  }, [user]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    // Reset between sessions / role switches.
    queueMicrotask(() => setUnreadCount(0));
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (!user) return undefined;

    queueMicrotask(() => refresh());

    const start = () => {
      if (timerRef.current) return;
      timerRef.current = setInterval(refresh, POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        refresh();
        start();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    if (!document.hidden) start();

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, [user, refresh]);

  return (
    <UnreadMessagesContext.Provider value={{ unreadCount, refresh }}>
      {children}
    </UnreadMessagesContext.Provider>
  );
};

export const useUnreadMessages = () => useContext(UnreadMessagesContext);

export default UnreadMessagesContext;
